// Routines for parsing the current state of the app from backend messages.

import { assert } from '~/utils/nut';
import type { Message } from './message';
import type { DatabaseSchema } from './databaseSchema';

// Message sent whenever the app summary is updated.
export const APP_SUMMARY_CATEGORY = 'AppSummary';

export enum PlaywrightTestStatus {
  Pass = 'Pass',
  Fail = 'Fail',
  NotRun = 'NotRun',
}

// Details of the app have a short name and a one sentence description.
export interface AppDetail {
  name: string;
  description: string;
}

// Describes the contents of a page in the app.
export interface AppPage {
  // Short name for the page. 7/24/2025: Older apps don't have this.
  name?: string;

  // Path to this page in the app. '/' for the root page, may include wildcards.
  path: string;

  // One sentence description of the page.
  description: string;

  // All components on the page. Name is the component class or function name.
  components: AppDetail[];
}

// Kinds of APIs a feature can define or use.
export enum AppAPIKind {
  // A serverless function which is defined in the backend and called by the frontend.
  ServerlessFunction = 'ServerlessFunction',

  // A function defined in library code and called by frontend components.
  Frontend = 'Frontend',

  // A function defined in library code and called by backend functions.
  Backend = 'Backend',
}

// An API defined or used by a feature.
export interface AppAPI extends AppDetail {
  kind: AppAPIKind;
}

// The status of a feature describes its implementation and whether associated components
// should be functional.
export enum AppFeatureStatus {
  // Not started and has an associated cost which must be paid before work can start.
  PaymentNeeded = 'PaymentNeeded',

  // Not started but work can start immediately.
  NotStarted = 'NotStarted',

  // Implementation has started on implementing the feature's functionality.
  ImplementationInProgress = 'ImplementationInProgress',

  // Functionality has finished implementation.
  Implemented = 'Implemented',

  // Implementation work for the feature failed after several attempts.
  Failed = 'Failed',
}

export enum AppFeatureKind {
  // Build the app's mockup.
  BuildMockup = 'BuildMockup',

  // Initial design work for the database and backend/reducer APIs.
  DesignAPIs = 'DesignAPIs',

  // Build the backend and components for the first functional version of the app.
  BuildInitialApp = 'BuildInitialApp',

  // Write one or more integration tests and get them to pass.
  IntegrationTests = 'IntegrationTests',

  // Bug fix
  FixBug = 'FixBug',
}

export interface AppFeature {
  // Short name for the feature. Unique within the app.
  name: string;

  // Kind of feature.
  kind?: AppFeatureKind;

  // Current status of the feature.
  status: AppFeatureStatus;

  // Time when this feature was created. 2025/8/30: Always present in newer apps.
  time?: string;

  // One sentence description of the feature.
  description: string;

  // One paragraph summary of the feature's requirements.
  summary: string;

  // Cost of this feature in peanuts. If not set, the feature is free.
  cost?: number;

  // Names of any components which the feature implements.
  componentNames?: string[];

  // Any APIs defined by other features and used by this feature.
  usedAPIs?: AppAPI[];

  // Any APIs defined by this feature.
  definedAPIs?: AppAPI[];

  // Any database changes needed by the feature.
  databaseChange?: DatabaseSchema;

  // Any secrets required by backend APIs in the feature.
  // Names are environment variables.
  secrets?: AppDetail[];

  // Tests for functionality added by the feature.
  tests?: AppTest[];
}

export enum BugReportStatus {
  // The bug is unresolved. It is either being worked on by a feature or has been
  // escalated to developer support.
  Open = 'Open',

  // Work finished on the bug, waiting for confirmation from the user
  // for whether the bug is resolved.
  WaitingForFeedback = 'WaitingForFeedback',

  // The user marked the bug as fixed.
  Resolved = 'Resolved',

  // The bug was not fixed successfully. The cost of the app was refunded.
  Failed = 'Failed',

  // The bug was canceled by the user.
  Canceled = 'Canceled',
}

export interface BugReport {
  // Unique name within the app for the bug report.
  name: string;

  // One sentence description of the problem with the app.
  description: string;

  // Current status of the bug report.
  status: BugReportStatus;

  // Any features that have attempted (or are still attempting) to fix this bug.
  featureNames: string[];

  // Any time at which the bug report was escalated to developer support.
  escalateTime?: string;
}

// Describes a planned or implemented playwright test.
export interface AppTest {
  title: string;

  // Set after the test has been implemented.
  status?: PlaywrightTestStatus;

  // Any recording from the last time the test ran.
  recordingId?: string;
}

export enum AppUpdateReasonKind {
  DescribeLayout = 'DescribeLayout',
  StartDesignAPIs = 'StartDesignAPIs',
  DesignAPIs = 'DesignAPIs',
  BuildInitialApp = 'BuildInitialApp',
  AddFeature = 'AddFeature',
  StartImplementFeature = 'StartImplementFeature',
  FeatureImplemented = 'FeatureImplemented',
  FeatureCanceled = 'FeatureCanceled',
  StartMockup = 'StartMockup',
  MockupImplemented = 'MockupImplemented',
  SetSecrets = 'SetSecrets',
  RevertApp = 'RevertApp',
  CopyApp = 'CopyApp',
  UpdateTemplate = 'UpdateTemplate',
  ManualUpdate = 'ManualUpdate',
  ResolveBugReport = 'ResolveBugReport',
  EscalateBugReport = 'EscalateBugReport',
  CancelBugReport = 'CancelBugReport',
}

// Describes why the app's summary was updated.
export interface AppUpdateReason {
  kind: AppUpdateReasonKind;

  // Any feature associated with the update.
  featureName?: string;

  // For RevertApp, the iteration the summary was reverted to.
  iteration?: number;

  // For CopyApp, the appId of the app which was copied.
  appId?: string;
}

export interface AppSummary {
  // Short and high level description of the app.
  description: string;

  pages?: AppPage[];
  navigation?: string;
  features?: AppFeature[];
  bugReports?: BugReport[];
  schema?: DatabaseSchema;
  secrets?: AppDetail[];
  setSecrets?: string[];

  // The repository being described, if available.
  repositoryId?: string;

  // Version of the main app template the repository uses.
  templateVersion?: string;

  // Version string of the repository: Major.Minor.Patch
  // The version advances every time the app changes.
  version?: string;

  iteration: number;
  time: string;
  chatId?: string;
  reason?: AppUpdateReason;
}

export function parseAppSummaryMessage(message: Message): AppSummary | undefined {
  try {
    assert(message.category === APP_SUMMARY_CATEGORY, 'Message is not an app summary message');
    const appSummary = JSON.parse(message.content) as AppSummary;
    assert(typeof appSummary.description === 'string', 'Missing app description');
    assert(Array.isArray(appSummary.pages), 'Missing app pages');
    return appSummary;
  } catch (e) {
    console.error('Failed to parse app summary message', e);
    return undefined;
  }
}

export function isFeatureStatusImplemented(status?: AppFeatureStatus) {
  return status && (status === AppFeatureStatus.Implemented || status === AppFeatureStatus.Failed);
}
